// Simplified UX-Friendly Under-13 Consent Form
// Single "I Agree" approach - COPPA compliant when all disclosures are present

// Features your child can do (informational - what they're consenting to)
export const CHILD_FEATURES = [
  {
    title: "Create & Save Artwork",
    description: "Your child can create, save, and organize all their amazing artwork in their private gallery"
  },
  {
    title: "Learn Art History",
    description: "Explore 500+ artworks and learn about techniques, styles, and art movements from around the world"
  },
  {
    title: "Daily Creative Activities",
    description: "Participate in fun, skill-building activities designed for young artists"
  },
  {
    title: "Story Creation",
    description: "Create stories with characters and illustrations using our storyboarding tools"
  },
  {
    title: "Co-Learning Spaces",
    description: "Share and learn from artworks other artists around the world in safe, moderated spaces"
  },
  {
    title: "Learn with Mentors",
    description: "Get guidance and feedback from approved art mentors and teachers"
  },
  {
    title: "Reimagine Artwork",
    description: "Explore creative variations and learn different art styles"
  }
];

// Privacy & Safety Features (Auto-applied - just inform parents)
export const PRIVACY_FEATURES = {
  title: "Privacy & Safety Features",
  items: [
    {
      label: "Profile Picture",
      value: "Avatar Selection Only",
      description: "Your child will select from fun avatars - no real photos are used or displayed"
    },
    {
      label: "Display Name",
      value: "Nickname Only",
      description: "Only the nickname provided at profile creation will be used for artwork display - real name is never shown"
    },
    {
      label: "Real Name",
      value: "Always Hidden",
      description: "Your child's real name is never displayed publicly or shared with other users"
    },
    {
      label: "Location",
      value: "Never Collected",
      description: "We don't collect or share location information"
    },
    {
      label: "Content Moderation",
      value: "Always Active",
      description: "All shared content is reviewed by our moderation team to ensure a safe environment"
    },
    {
      label: "Sharing",
      value: "Co-Learning Spaces Only",
      description: "Artwork is only shared in controlled, moderated co-learning spaces with other artists and approved mentors"
    }
  ]
};

// What information we collect (COPPA disclosure - collapsible)
export const DATA_COLLECTION = {
  title: "What Information We Collect",
  items: [
    "Account information (name, email, date of birth, age) - required for account management",
    "Creative content (artwork, stories, activity submissions) - to provide learning features",
    "Profile information (nickname, avatar selection) - for personalization",
    "Usage data (features accessed, time spent) - to improve the platform"
  ],
  note: "All information is stored securely and used only to provide educational services."
};

// How we use information (COPPA disclosure - collapsible)
export const DATA_USAGE = {
  title: "How We Use Your Child's Information",
  items: [
    "To create and manage your child's account",
    "To provide educational and creative features",
    "To personalize the learning experience",
    "To enable co-learning and community features",
    "To ensure platform security and safety",
    "To communicate about account activity and important updates",
    "Creative art education features are enabled via third-party AI models moderated by Artmosphere technology"
  ]
};

// Third-party services (COPPA disclosure - collapsible)
export const THIRD_PARTY_SERVICES = {
  title: "Third-Party Services",
  items: [
    "Firebase (Google) - for secure data storage and authentication",
    "Payment processors - for subscription management (payment info not stored by Artmosphere)",
    "Email services - for account-related communications",
    "AI models - Creative art education features are enabled via third-party AI models moderated by Artmosphere technology"
  ],
  note: "All third-party services are contractually required to protect your child's information and comply with COPPA."
};

// Parental rights (COPPA disclosure)
export const PARENTAL_RIGHTS = {
  title: "Your Parental Rights",
  items: [
    {
      text: "Review your child's account and activity anytime"
    },
    {
      text: "Change privacy settings for artworks shared anytime"
    },
    {
      text: "Delete your child's account and all data anytime"
    },
    {
      text: "Contact us at support@artmosphere.com with any questions or to revoke consent"
    }
  ]
};

// Consent mapping for backend (all privacy settings auto-applied)
export const CONSENT_MAPPING = {
  // Co-learning is enabled by default (important feature)
  artwork_colearning_allowed: true,
  artwork_public_allowed: false, // Only co-learning, not public
  pii_public_allowed: false, // Real name always hidden
  profile_pic_allowed: false, // Always use avatar
  account_info_collection: true, // Required
  content_collection: true, // Required
  profile_info_collection: true, // For nickname and avatar
  usage_data_collection: true, // Default on
  third_party_sharing: true, // Required for service operation
  marketing_communications: false // Opt-in only
};

// COPPA compliance note
export const COPPA_NOTE = {
  title: "COPPA Compliance",
  text: "This consent form complies with the Children's Online Privacy Protection Act (COPPA). By clicking 'I Agree', you are providing verifiable parental consent for your child under 13 to use Artmosphere with the features and privacy settings described above."
};
